import { Component, inject, OnInit } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { AuthService } from '../../shared/services/auth.service';
import { ActivatedRoute, Router } from '@angular/router';

/** Gives the toast time to actually be read before the redirect fires. */
const SUCCESS_REDIRECT_DELAY_MS = 1500;

@Component({
  selector: 'app-update-password',
  standalone: true,
  imports: [FormsModule, ToastModule],
  providers: [MessageService],
  templateUrl: './update-password.component.html',
  styleUrl: './update-password.component.css',
})
export class UpdatePasswordComponent implements OnInit {
  private authService = inject(AuthService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private messageService = inject(MessageService);

  newPassword = '';
  confirmPassword = '';
  email = '';
  token = '';
  loading = false;
  errorMessage = '';
  showNewPassword = false;
  showConfirmPassword = false;

  ngOnInit() {
    this.token = this.route.snapshot.queryParamMap.get('token') || '';
    this.email = localStorage.getItem('verifyEmail') || '';
  }

  updatePassword() {
    this.errorMessage = '';

    if (!this.newPassword || !this.confirmPassword) {
      this.errorMessage = 'Please fill in both password fields.';
      return;
    }
    if (this.newPassword !== this.confirmPassword) {
      this.errorMessage = 'Passwords do not match.';
      return;
    }
    if (this.newPassword.length < 8) {
      this.errorMessage = 'Password must be at least 8 characters.';
      return;
    }

    this.loading = true;

    // ── Invite flow (token in URL) ─────────────────────────────────────────
    if (this.token) {
      this.authService
        .acceptInvite({
          token: this.token,
          password: this.newPassword,
          confirmPassword: this.confirmPassword,
        })
        .subscribe({
          next: () => {
            this.loading = false;
            this._showSuccessAndRedirect('Your password has been set. Please log in.');
          },
          error: (err: any) => {
            this.loading = false;
            const message = err?.error?.message ?? 'Failed to set password. Please try again.';
            this.errorMessage = message;
            this._showError(message);
          },
        });
      return;
    }

    // ── Forgot-password flow (email stored from OTP step) ─────────────────
    // Uses /reset-password with email, no auth token required.
    this.authService.resetPasswordByEmail(this.email, this.newPassword).subscribe({
      next: () => {
        this.loading = false;
        localStorage.removeItem('authFlow');
        localStorage.removeItem('verifyEmail');
        this._showSuccessAndRedirect('Your password has been reset. Please log in.');
      },
      error: (err: any) => {
        this.loading = false;
        const message = err?.error?.message ?? 'Failed to reset password. Please try again.';
        this.errorMessage = message;
        this._showError(message);
      },
    });
  }

  /**
   * Without this, a successful reset silently navigated straight to
   * /auth/login with no feedback at all, the user had no way to tell it
   * actually worked. MessageService/p-toast are provided locally on this
   * component (like each portal section does for its own themed toasts),
   * so the redirect is deliberately delayed just long enough for the toast
   * to actually be read before this page (and its toast) gets torn down.
   */
  private _showSuccessAndRedirect(detail: string): void {
    this.messageService.add({
      severity: 'success',
      summary: 'Password Updated',
      detail,
      life: SUCCESS_REDIRECT_DELAY_MS + 2500,
    });
    setTimeout(() => this.router.navigate(['/auth/login']), SUCCESS_REDIRECT_DELAY_MS);
  }

  private _showError(detail: string): void {
    this.messageService.add({ severity: 'error', summary: 'Update Failed', detail });
  }
}
